import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Alert, Box, Button, ButtonBase, Chip, Collapse, Link, Typography } from '@mui/material';
import { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import PostService from '../../services/PostService';
import AutosaveText from '../work/AutosaveText';
import CopyButton from '../work/CopyButton';
import { workFolderUrl } from '../work/WorkFolderSection';
import { shortName } from './taskUi';

const TEXT_STAGES = ['TRANSLATE_TEXT', 'TEXT_QC'];
// Over this many languages the strip would push the work off the screen
const STRIP_OPEN_LIMIT = 4;
const PARTS = { DESIGN: 'Text on design', CAPTION: 'Caption', SUBTITLES: 'Subtitles' };
const BOX_FONT = { fontSize: 16 };

/** Text QC and later stages show the translator what changed. */
const WhatChanged = ({ box, shownTo }) => {
    const [open, setOpen] = useState(false);
    if (!box?.before || !shownTo) return null;
    return (
        <Typography variant="caption" color="text.secondary" component="div">
            Edited by {shortName(box.editedBy)} at {box.editedStage} ·{' '}
            <Link component="button" variant="caption" onClick={() => setOpen(!open)} aria-expanded={open}>
                See what changed
            </Link>
            <Collapse in={open}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                    <b>Before:</b> {box.before}
                </Typography>
            </Collapse>
        </Typography>
    );
};

export const ReadOnlyText = ({ label, text, note, copy }) => (
    <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            {copy && <CopyButton text={text} ariaLabel={`Copy ${label}`} />}
        </Box>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {text || <span style={{ opacity: 0.6 }}>Empty</span>}
        </Typography>
        {note && (
            <Typography variant="caption" color="text.secondary" component="div">
                {note}
            </Typography>
        )}
    </Box>
);

/** What AV pastes into the post, with one big button, since a phone is where they do it. */
export const CaptionFirst = ({ language, text }) => (
    <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary" component="div">
            Caption ({language})
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mb: 1 }}>
            {text || <span style={{ opacity: 0.6 }}>Empty</span>}
        </Typography>
        <CopyButton text={text} label="Copy caption" ariaLabel={`Copy caption, ${language}`} sx={{ minHeight: 44 }} />
    </Box>
);

/** Replication copies the design text a slide at a time. */
const DesignLines = ({ text }) => {
    const lines = (text || '').split('\n').filter((l) => l.trim());
    return (
        <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    Text on design
                </Typography>
                <CopyButton text={text} label="Copy all" ariaLabel="Copy all of Text on design" />
            </Box>
            {lines.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                    Empty
                </Typography>
            )}
            {lines.map((line, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, py: 0.25 }}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {line}
                    </Typography>
                    <CopyButton text={line} ariaLabel={`Copy line ${i + 1}`} />
                </Box>
            ))}
        </Box>
    );
};

const FolderLink = ({ task }) => (
    <Button
        size="small"
        variant="outlined"
        endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
        href={workFolderUrl(task.contentId, task.languageKey, 'POST')}
        target="_blank"
        rel="noopener"
        sx={{ mt: 1, mr: 1 }}
    >
        Open shared folder
    </Button>
);

export const CanvaLink = ({ url }) =>
    url ? (
        <Button
            size="small"
            variant="outlined"
            endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1, mr: 1 }}
        >
            Open in Canva
        </Button>
    ) : null;

/** The English the language is translated from, folded to two lines away from the text stages. */
const EnglishText = ({ post, folded }) => {
    const [open, setOpen] = useState(false);
    const text = [post.englishDesign, post.englishCaption].filter(Boolean).join('\n\n');
    if (folded && !open) {
        return (
            <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                    English
                </Typography>
                <Typography
                    variant="body2"
                    sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                >
                    {text}
                </Typography>
                <Link component="button" variant="caption" onClick={() => setOpen(true)}>
                    Show all
                </Link>
            </Box>
        );
    }
    return (
        <Box>
            {post.englishDesign && <ReadOnlyText label="English Text on design" text={post.englishDesign} />}
            {post.englishCaption && <ReadOnlyText label="English Caption" text={post.englishCaption} />}
        </Box>
    );
};

/**
 * The strip under the drawer's header: every language of the post. With more than 4 it shows only
 * yours, then one line; PMs, admins and AV start with every language open.
 */
export const PostLanguageStrip = ({ post, task, canManage, onOpen }) => {
    const [showAll, setShowAll] = useState(canManage || post.seesEveryLanguage);
    const strip = post.strip || [];
    if (strip.length < 2) return null;
    const long = strip.length > STRIP_OPEN_LIMIT;
    const mine = strip.filter((e) => e.mine || e.taskId === task.id);
    const shown = !long || showAll ? strip : mine;
    const rest = strip.filter((e) => !shown.includes(e));
    const summary = () => {
        const counts = {};
        rest.forEach((e) => {
            const key = e.state === 'DONE' ? 'done' : `at ${e.stageName}`;
            counts[key] = (counts[key] || 0) + 1;
        });
        return Object.entries(counts)
            .map(([k, n]) => `${n} ${k}`)
            .join(', ');
    };
    const line = (e) => {
        if (e.state === 'DONE') return 'Done';
        if (e.state === 'CANCELLED') return 'Cancelled';
        const who = e.mine ? 'yours' : e.assignee ? shortName(e.assignee) : null;
        return [e.stageName, who, e.state === 'SENT_BACK' ? 'Sent back' : e.state === 'OVERDUE' ? 'Overdue' : null]
            .filter(Boolean)
            .join(' · ');
    };
    return (
        <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }} role="list" aria-label="Languages of this post">
            {shown.map((e) => {
                const current = e.taskId === task.id;
                return (
                    <Box key={e.languageKey} role="listitem">
                        <ButtonBase
                            disabled={!e.canOpen || current}
                            aria-current={current ? 'true' : undefined}
                            onClick={() => onOpen(e.taskId)}
                            sx={{
                                border: 1,
                                borderColor: current ? 'primary.main' : 'divider',
                                borderRadius: 1,
                                px: 1,
                                py: 0.5,
                                textAlign: 'left',
                                display: 'block',
                                opacity: e.canOpen || current ? 1 : 0.7,
                            }}
                        >
                            <Typography variant="body2" component="span" sx={{ display: 'block', fontWeight: current ? 600 : 400 }}>
                                {e.languageLabel}
                            </Typography>
                            <Typography
                                variant="caption"
                                color={e.state === 'SENT_BACK' ? 'warning.main' : 'text.secondary'}
                                component="span"
                                sx={{ display: 'block' }}
                            >
                                {line(e)}
                            </Typography>
                        </ButtonBase>
                    </Box>
                );
            })}
            {long && rest.length > 0 && (
                <Typography variant="caption" color="text.secondary" role="listitem" sx={{ width: '100%' }}>
                    {rest.length} other language{rest.length === 1 ? '' : 's'}: {summary()} ·{' '}
                    <Link component="button" variant="caption" onClick={() => setShowAll(true)}>
                        Show all
                    </Link>
                </Typography>
            )}
        </Box>
    );
};

/**
 * The drawer's Post text section. What it shows depends on the stage and who opens it. flush()
 * waits for the boxes' last saves; values() gives what's in them, for the ready warning.
 */
const PostTextSection = forwardRef(function PostTextSection({ detail, task, current, onReload, isMe }, ref) {
    const post = detail.post;
    const designBox = useRef(null);
    const captionBox = useRef(null);
    const canvaBox = useRef(null);
    // A box held by someone else's change starts again from what's saved
    const [generation, setGeneration] = useState(0);
    const reload = async () => {
        await onReload();
        setGeneration((g) => g + 1);
    };
    const stageKey = current?.stageKey;
    const textStage = TEXT_STAGES.includes(stageKey);
    const english = task.languageKey === 'eng';
    const designText = english ? post.englishDesign : post.design.value;
    const captionText = english ? post.englishCaption : post.caption.value;
    const canTranslate = post.canEditText && !english;
    const isTranslator = useMemo(
        () => detail.stages.some((s) => s.stageKey === 'TRANSLATE_TEXT' && isMe(s.assignedTo)) || detail.permissions.canManage,
        [detail.stages, detail.permissions.canManage, isMe],
    );

    useImperativeHandle(ref, () => ({
        flush: async () => {
            const results = await Promise.all(
                [designBox, captionBox, canvaBox].map((r) => (r.current ? r.current.flush() : true)),
            );
            return results.every(Boolean);
        },
        values: () => ({
            design: designBox.current ? designBox.current.value() : designText,
            caption: captionBox.current ? captionBox.current.value() : captionText,
        }),
    }));

    const save = (field) => (value, rev) => PostService.saveBox(post.postId, task.languageKey, { field, value, rev });
    const box = (field, boxRef, label, data, minRows) => (
        <Box sx={{ mt: 1.5 }}>
            <AutosaveText
                key={`${task.id}-${field}-${generation}`}
                ref={boxRef}
                label={label}
                initial={data.value}
                rev={data.rev}
                minRows={minRows}
                onSave={save(field)}
                onReload={reload}
                shortName={shortName}
                inputSx={BOX_FONT}
            />
            <WhatChanged box={data} shownTo={isTranslator} />
        </Box>
    );

    const editableText = canTranslate && (textStage || detail.permissions.canManage);
    // On a video post the subtitles take the place of Text on design
    const needsDesign = !post.subtitleVideo && (!!post.englishDesign || !!post.design.value);
    const needsCaption = !!post.englishCaption || !!post.caption.value;
    const canva = post.canvaUrl.value;

    const languageColumn = editableText ? (
        <>
            {needsDesign && box('design', designBox, `Text on design (${task.languageLabel})`, post.design, 3)}
            {needsCaption && box('caption', captionBox, `Caption (${task.languageLabel})`, post.caption, 4)}
        </>
    ) : (
        <>
            {needsDesign && <ReadOnlyText label={`Text on design (${task.languageLabel})`} text={designText} copy />}
            {needsCaption && <ReadOnlyText label={`Caption (${task.languageLabel})`} text={captionText} copy />}
        </>
    );

    return (
        <Box sx={{ mt: 2.5 }}>
            <Typography variant="overline" component="div">
                Post text
            </Typography>
            {post.sentBack && (
                <Alert severity="warning" sx={{ mb: 1 }}>
                    Sent back by {shortName(post.sentBack.by)}
                    {post.sentBack.fromStage ? ` at ${post.sentBack.fromStage}` : ''}: "{post.sentBack.comment}"
                    {post.sentBack.parts?.length > 0 && (
                        <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5 }}>
                            {post.sentBack.parts.map((p) => (
                                <Chip key={p} size="small" label={PARTS[p] || p} />
                            ))}
                        </Box>
                    )}
                </Alert>
            )}
            {post.englishChanged && (
                <Alert severity="info" sx={{ mb: 1 }}>
                    The English changed.
                    <EnglishWas post={post} />
                </Alert>
            )}

            {textStage ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <EnglishText post={post} />
                    <Box>{languageColumn}</Box>
                </Box>
            ) : (
                <>
                    {!english && <EnglishText post={post} folded />}
                    {stageKey === 'REPLICATION' ? (
                        <>
                            {needsDesign &&
                                (editableText ? (
                                    box('design', designBox, `Text on design (${task.languageLabel})`, post.design, 3)
                                ) : (
                                    <DesignLines text={designText} />
                                ))}
                            {post.subtitleVideo?.onVideo && (
                                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
                                    Use Download SRT in the editor, so you have the latest subtitles.
                                </Typography>
                            )}
                            {needsCaption &&
                                (editableText ? (
                                    box('caption', captionBox, `Caption (${task.languageLabel})`, post.caption, 4)
                                ) : (
                                    <ReadOnlyText label={`Caption (${task.languageLabel})`} text={captionText} note="Not on the design" copy />
                                ))}
                        </>
                    ) : (
                        languageColumn
                    )}
                    {post.canEditCanva && stageKey === 'REPLICATION' && (
                        <Box sx={{ mt: 1.5 }}>
                            <AutosaveText
                                key={`${task.id}-canva-${generation}`}
                                ref={canvaBox}
                                label="Canva link (optional)"
                                placeholder="https://"
                                initial={canva}
                                rev={post.canvaUrl.rev}
                                multiline={false}
                                onSave={save('canvaUrl')}
                                onReload={reload}
                                shortName={shortName}
                            />
                        </Box>
                    )}
                    <Box>
                        <CanvaLink url={canva} />
                        {post.canSeeFolder && <FolderLink task={task} />}
                    </Box>
                    {post.secondRound && detail.workFolder?.stageFiles === 0 && ['DUBBING', 'REPLICATION'].includes(stageKey) && (
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            No new file since this was sent back.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
});

const changed = (was, now) => (was || '') !== (now || '');

/** Only the box that changed; one that was empty before says so. */
const EnglishWas = ({ post }) => {
    const [open, setOpen] = useState(false);
    return (
        <>
            {' '}
            <Link component="button" variant="body2" onClick={() => setOpen(!open)} aria-expanded={open}>
                See what changed
            </Link>
            <Collapse in={open}>
                {changed(post.previousEnglishDesign, post.englishDesign) && (
                    <ReadOnlyText label="Text on design was" text={post.previousEnglishDesign} />
                )}
                {changed(post.previousEnglishCaption, post.englishCaption) && (
                    <ReadOnlyText label="Caption was" text={post.previousEnglishCaption} />
                )}
            </Collapse>
        </>
    );
};

export default PostTextSection;
